import { useSearchParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { BASE_URL } from "../../config/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get("token");
  const emailFromUrl = params.get("email");

  const [email] = useState(emailFromUrl || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [pwdFocused, setPwdFocused] = useState(false);
  const [error, setError] = useState("");

  /* ================= INVALID TOKEN ================= */
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow p-6 text-center">
          <div className="mx-auto mb-4 h-14 w-14 flex items-center justify-center rounded-full bg-rose-100 text-rose-600">
            <XCircle size={28} />
          </div>

          <h2 className="text-lg font-semibold text-slate-800">
            Link expired or invalid
          </h2>

          <p className="mt-2 text-sm text-slate-500">
            This password reset link is no longer valid.
            <br />
            Please request a new reset link.
          </p>

          <button
            onClick={() => navigate("/login")}
            className="mt-6 w-full rounded-xl bg-blue-600 py-2 text-white font-semibold hover:bg-blue-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  /* ================= PASSWORD RULES ================= */
  const checks = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };

  const strengthScore = Object.values(checks).filter(Boolean).length;

  const strengthLabel =
    strengthScore <= 1
      ? "Very Weak"
      : strengthScore === 2
      ? "Weak"
      : strengthScore === 3
      ? "Good"
      : "Strong";

  const strengthColor =
    strengthScore <= 1
      ? "bg-rose-500"
      : strengthScore === 2
      ? "bg-amber-500"
      : strengthScore === 3
      ? "bg-blue-500"
      : "bg-emerald-500";

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (strengthScore < 4) {
      setError("Password does not meet all requirements");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setLoading(true);

      await axios.post(`${BASE_URL}warranty/company/password/reset`, {
        contact_email: email,
        token,
        password,
        password_confirmation: confirmPassword,
      });

      navigate("/login");
    } catch (err) {
      setError(err?.response?.data?.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md bg-white p-6 rounded-2xl shadow space-y-4"
      >
        <h2 className="text-xl font-semibold text-slate-800">
          Reset Password
        </h2>

        {/* Email */}
        <div>
          <label className="text-sm font-medium">Email</label>
          <input
            value={email}
            readOnly
            className="w-full mt-1 px-3 py-2 border rounded-lg bg-slate-100"
          />
        </div>

        {/* New Password */}
        <div>
          <label className="text-sm font-medium">New Password</label>

          <div className="relative">
            <input
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setPwdFocused(true)}
              onBlur={() => setPwdFocused(false)}
              className="w-full mt-1 px-3 py-2 border rounded-lg pr-10"
            />

            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
            >
              {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Strength bar */}
          {password && (
            <div className="mt-2">
              <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                <div
                  className={`h-full transition-all ${strengthColor}`}
                  style={{ width: `${(strengthScore / 4) * 100}%` }}
                />
              </div>
              <p className="mt-1 text-xs font-semibold text-slate-600">
                Strength:{" "}
                <span className="font-bold">{strengthLabel}</span>
              </p>
            </div>
          )}

          {/* Requirements */}
          {pwdFocused && (
            <div className="mt-3 rounded-xl border bg-slate-50 p-3 text-xs space-y-1">
              <Rule ok={checks.length} text="At least 8 characters" />
              <Rule ok={checks.upper} text="One uppercase letter" />
              <Rule ok={checks.lower} text="One lowercase letter" />
              <Rule ok={checks.number} text="One number" />
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="text-sm font-medium">Confirm Password</label>

          <div className="relative">
            <input
              type={showConfirmPwd ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg pr-10"
            />

            <button
              type="button"
              onClick={() => setShowConfirmPwd((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
            >
              {showConfirmPwd ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {error && (
          <p className="text-sm font-semibold text-rose-600">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading || strengthScore < 4}
          className="w-full py-2 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-50"
        >
          {loading ? "Resetting..." : "Reset Password"}
        </button>
      </form>
    </div>
  );
}

/* ================= HELPERS ================= */
function Rule({ ok, text }) {
  return (
    <p
      className={`flex items-center gap-2 ${
        ok ? "text-emerald-600" : "text-slate-500"
      }`}
    >
      {ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
      {text}
    </p>
  );
}



// import { useSearchParams, useNavigate } from "react-router-dom";
// import { useState } from "react";
// import axios from "axios";
// import { BASE_URL } from "../../config/api";

// export default function ResetPassword() {
//   const [params] = useSearchParams();
//   const navigate = useNavigate();

//   const token = params.get("token");
//   const emailFromUrl = params.get("email");

//   const [email] = useState(emailFromUrl || "");
//   const [password, setPassword] = useState("");
//   const [confirmPassword, setConfirmPassword] = useState("");
//   const [loading, setLoading] = useState(false);

//   const [showPwd, setShowPwd] = useState(false);
// const [showConfirmPwd, setShowConfirmPwd] = useState(false);
// const [error, setError] = useState("");


//   if (!token) {
//   return (
//     <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
//       <div className="w-full max-w-md bg-white rounded-2xl shadow p-6 text-center">
//         <div className="mx-auto mb-4 h-14 w-14 flex items-center justify-center rounded-full bg-rose-100 text-rose-600 text-2xl">
//           ❌
//         </div>

//         <h2 className="text-lg font-semibold text-slate-800">
//           Link expired or invalid
//         </h2>

//         <p className="mt-2 text-sm text-slate-500">
//           This password reset link is no longer valid.
//           <br />
//           Please request a new reset link to continue.
//         </p>

//         <button
//           onClick={() => navigate("/login")}
//           className="mt-6 w-full rounded-xl bg-blue-600 py-2 text-white font-semibold hover:bg-blue-700 transition"
//         >
//           Back to Login
//         </button>
//       </div>
//     </div>
//   );
// }

//     const validatePassword = (pwd) => {
//   const minLength = pwd.length >= 8;
//   const hasUpper = /[A-Z]/.test(pwd);
//   const hasLower = /[a-z]/.test(pwd);
//   const hasNumber = /[0-9]/.test(pwd);

//   if (!minLength) return "Password must be at least 8 characters";
//   if (!hasUpper) return "Password must include an uppercase letter";
//   if (!hasLower) return "Password must include a lowercase letter";
//   if (!hasNumber) return "Password must include a number";

//   return "";
// };


//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!password || password.length < 6) {
//       alert("Password must be at least 6 characters");
//       return;
//     }

//     setError("");

//     const pwdError = validatePassword(password);
//     if (pwdError) {
//     setError(pwdError);
//     return;
//     }

//     if (password !== confirmPassword) {
//     setError("Passwords do not match");
//     return;
//     }


//     try {
//       setLoading(true);

//       await axios.post(`${BASE_URL}warranty/company/password/reset`, {
//         contact_email: email,
//         token,          // ✅ from URL
//         password,
//         password_confirmation: confirmPassword,
//       });

//       alert("Password reset successful");
//       navigate("/login");
//     } catch (err) {
//       alert(err?.response?.data?.message || "Reset failed");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="min-h-screen flex items-center justify-center bg-slate-50">
//       <form
//         onSubmit={handleSubmit}
//         className="w-full max-w-md bg-white p-6 rounded-2xl shadow"
//       >
//         <h2 className="text-xl font-semibold mb-4">Reset Password</h2>

//         {/* Email (readonly) */}
//         <div className="mb-3">
//           <label className="text-sm font-medium">Email</label>
//           <input
//             value={email}
//             readOnly
//             className="w-full mt-1 px-3 py-2 border rounded-lg bg-slate-100"
//           />
//         </div>

//         {/* New Password */}
//         <div className="mb-3">
//             <label className="text-sm font-medium">New Password</label>

//             <div className="relative">
//                 <input
//                 type={showPwd ? "text" : "password"}
//                 value={password}
//                 onChange={(e) => setPassword(e.target.value)}
//                 className="w-full mt-1 px-3 py-2 border rounded-lg pr-10"
//                 />

//                 <button
//                 type="button"
//                 onClick={() => setShowPwd(s => !s)}
//                 className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
//                 >
//                 {showPwd ? "🙈" : "👁️"}
//                 </button>
//             </div>
//             </div>


//         {/* Confirm Password */}
//         <div className="mb-4">
//             <label className="text-sm font-medium">Confirm Password</label>

//             <div className="relative">
//                 <input
//                 type={showConfirmPwd ? "text" : "password"}
//                 value={confirmPassword}
//                 onChange={(e) => setConfirmPassword(e.target.value)}
//                 className="w-full mt-1 px-3 py-2 border rounded-lg pr-10"
//                 />

//                 <button
//                 type="button"
//                 onClick={() => setShowConfirmPwd(s => !s)}
//                 className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
//                 >
//                 {showConfirmPwd ? "🙈" : "👁️"}
//                 </button>
//             </div>
//             </div>


//         <button
//           type="submit"
//           disabled={loading}
//           className="w-full py-2 rounded-xl bg-blue-600 text-white font-semibold"
//         >
//           {loading ? "Resetting..." : "Reset Password"}
//         </button>
//       </form>
//     </div>
//   );
// }



// import React, { useState } from "react";
// import { useSearchParams, useNavigate } from "react-router-dom";
// import axios from "axios";
// import { Lock, Mail, Key, Loader2, CheckCircle } from "lucide-react";

// import AuthLayout from "../../Component/Auth/AuthLayout";
// import GlassCard from "../../Component/Auth/GlassCard";
// import AuthHeader from "../../Component/Auth/AuthHeader";
// import { BASE_URL } from "../../config/api";

// export default function ResetPassword() {
//   const navigate = useNavigate();
//   const [params] = useSearchParams();

//   const [email, setEmail] = useState(params.get("email") || "");
//   const [token, setToken] = useState(params.get("token") || "");
//   const [password, setPassword] = useState("");
//   const [confirmPassword, setConfirmPassword] = useState("");

//   const [loading, setLoading] = useState(false);
//   const [msg, setMsg] = useState("");
//   const [success, setSuccess] = useState(false);

//   const validate = () => {
//     if (!email) return "Email is required";
//     if (!token) return "Invalid or missing token";
//     if (password.length < 6) return "Password must be at least 6 characters";
//     if (password !== confirmPassword) return "Passwords do not match";
//     return "";
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setMsg("");

//     const err = validate();
//     if (err) {
//       setMsg(err);
//       return;
//     }

//     try {
//       setLoading(true);

//       await axios.post(`${BASE_URL}common/reset-password`, {
//         email,
//         token,
//         password,
//         password_confirmation: confirmPassword,
//       });

//       setSuccess(true);

//       setTimeout(() => {
//         navigate("/");
//       }, 2000);
//     } catch (err) {
//       setMsg(
//         err?.response?.data?.message || "Failed to reset password"
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <AuthLayout showIllustration>
//       <GlassCard>
//         <AuthHeader
//           title="Reset Password"
//           subtitle="Create a new secure password"
//         />

//         {success ? (
//           <div className="flex flex-col items-center gap-3 py-6">
//             <CheckCircle className="text-emerald-500" size={40} />
//             <p className="text-sm font-semibold text-emerald-600">
//               Password reset successfully
//             </p>
//             <p className="text-xs text-slate-500">
//               Redirecting to login…
//             </p>
//           </div>
//         ) : (
//           <form onSubmit={handleSubmit} className="space-y-4">

//             {/* Email */}
//             <div>
//               <label className="text-sm font-medium text-slate-700">
//                 Email
//               </label>
//               <div className="relative rounded-xl border bg-white/60 px-3">
//                 <Mail size={16} className="absolute left-3 top-3 text-slate-400" />
//                 <input
//                   value={email}
//                   onChange={(e) => setEmail(e.target.value)}
//                   className="w-full bg-transparent pl-6 py-2 text-sm outline-none"
//                 />
//               </div>
//             </div>

//             {/* Token */}
//             <div>
//               <label className="text-sm font-medium text-slate-700">
//                 Reset Token
//               </label>
//               <div className="relative rounded-xl border bg-white/60 px-3">
//                 <Key size={16} className="absolute left-3 top-3 text-slate-400" />
//                 <input
//                   value={token}
//                   onChange={(e) => setToken(e.target.value)}
//                   className="w-full bg-transparent pl-6 py-2 text-sm outline-none"
//                 />
//               </div>
//             </div>

//             {/* New Password */}
//             <div>
//               <label className="text-sm font-medium text-slate-700">
//                 New Password
//               </label>
//               <div className="relative rounded-xl border bg-white/60 px-3">
//                 <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
//                 <input
//                   type="password"
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   className="w-full bg-transparent pl-6 py-2 text-sm outline-none"
//                 />
//               </div>
//             </div>

//             {/* Confirm Password */}
//             <div>
//               <label className="text-sm font-medium text-slate-700">
//                 Confirm Password
//               </label>
//               <div className="relative rounded-xl border bg-white/60 px-3">
//                 <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
//                 <input
//                   type="password"
//                   value={confirmPassword}
//                   onChange={(e) => setConfirmPassword(e.target.value)}
//                   className="w-full bg-transparent pl-6 py-2 text-sm outline-none"
//                 />
//               </div>
//             </div>

//             {msg && (
//               <p className="text-xs font-semibold text-rose-500">
//                 {msg}
//               </p>
//             )}

//             <button
//               type="submit"
//               disabled={loading}
//               className="
//                 w-full flex items-center justify-center gap-2
//                 rounded-xl py-2 font-semibold text-white
//                 bg-gradient-to-r from-blue-600 to-indigo-600
//                 hover:from-blue-700 hover:to-indigo-700
//                 disabled:opacity-60
//               "
//             >
//               {loading ? (
//                 <>
//                   <Loader2 className="animate-spin" size={16} />
//                   Resetting…
//                 </>
//               ) : (
//                 "Reset Password"
//               )}
//             </button>
//           </form>
//         )}
//       </GlassCard>
//     </AuthLayout>
//   );
// }
